import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, IconButton } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CodeIcon from '@mui/icons-material/Code';
import SchoolIcon from '@mui/icons-material/School';
import QuizIcon from '@mui/icons-material/Quiz';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { styled } from '@mui/material/styles';

const SliderContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: '100%',
  height: '600px',
  overflow: 'hidden',
  marginBottom: 0,
  [theme.breakpoints.down('md')]: {
    height: '500px',
  },
  [theme.breakpoints.down('sm')]: {
    height: '450px',
  },
}));

const SlideImage = styled('img')({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  objectPosition: 'center',
  zIndex: 0,
  opacity: 0.3,
  filter: 'blur(2px) brightness(0.4)',
});

const Slide = styled(Box)<{ active?: boolean; gradient?: string }>(({ theme, active, gradient }) => ({
  position: 'absolute',
  width: '100%',
  height: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: gradient || 'linear-gradient(135deg, rgba(99, 102, 241, 0.15) 0%, rgba(236, 72, 153, 0.15) 100%)',
  transition: 'opacity 0.8s cubic-bezier(0.4, 0, 0.2, 1), transform 0.8s cubic-bezier(0.4, 0, 0.2, 1)',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'radial-gradient(circle at 20% 50%, rgba(99, 102, 241, 0.1) 0%, transparent 50%), radial-gradient(circle at 80% 80%, rgba(236, 72, 153, 0.1) 0%, transparent 50%)',
    pointerEvents: 'none',
    zIndex: 1,
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'linear-gradient(180deg, rgba(15, 23, 42, 0.7) 0%, rgba(15, 23, 42, 0.95) 100%)',
    pointerEvents: 'none',
    zIndex: 1,
  },
  ...(active
    ? {
        opacity: 1,
        zIndex: 2,
        transform: 'scale(1)',
      }
    : {
        opacity: 0,
        zIndex: 1,
        transform: 'scale(1.05)',
      }),
}));

const SlideContent = styled(Box)(({ theme }) => ({
  position: 'relative',
  zIndex: 3,
  textAlign: 'center',
  maxWidth: '1200px',
  width: '100%',
  px: theme.spacing(4),
  py: theme.spacing(4),
  [theme.breakpoints.down('sm')]: {
    px: theme.spacing(2),
    py: theme.spacing(2),
  },
}));

const NavigationButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  top: '50%',
  transform: 'translateY(-50%)',
  zIndex: 10,
  background: 'rgba(15, 23, 42, 0.8)',
  backdropFilter: 'blur(20px)',
  border: '1px solid rgba(148, 163, 184, 0.2)',
  color: 'white',
  width: 56,
  height: 56,
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    background: 'rgba(99, 102, 241, 0.3)',
    borderColor: 'rgba(99, 102, 241, 0.5)',
    transform: 'translateY(-50%) scale(1.1)',
    boxShadow: '0 8px 24px rgba(99, 102, 241, 0.4)',
  },
  '&.prev': {
    left: theme.spacing(4),
    [theme.breakpoints.down('sm')]: {
      left: theme.spacing(2),
      width: 48,
      height: 48,
    },
  },
  '&.next': {
    right: theme.spacing(4),
    [theme.breakpoints.down('sm')]: {
      right: theme.spacing(2),
      width: 48,
      height: 48,
    },
  },
}));

const DotsContainer = styled(Box)(({ theme }) => ({
  position: 'absolute',
  bottom: theme.spacing(4),
  left: '50%',
  transform: 'translateX(-50%)',
  display: 'flex',
  gap: theme.spacing(1.5),
  zIndex: 10,
  [theme.breakpoints.down('sm')]: {
    bottom: theme.spacing(2),
  },
}));

const Dot = styled(Box)<{ active?: boolean }>(({ theme, active }) => ({
  width: active ? 40 : 10,
  height: 10,
  borderRadius: 5,
  background: active
    ? 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)'
    : 'rgba(148, 163, 184, 0.4)',
  cursor: 'pointer',
  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
  boxShadow: active ? '0 4px 12px rgba(99, 102, 241, 0.4)' : 'none',
  '&:hover': {
    background: active
      ? 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)'
      : 'rgba(148, 163, 184, 0.6)',
    transform: 'scale(1.2)',
  },
}));

interface SlideData {
  title: string;
  subtitle: string;
  description: string;
  icon: React.ReactNode;
  gradient: string;
  image?: string;
  buttonText?: string;
  buttonAction?: () => void;
}

interface HomeSliderProps {
  slides?: SlideData[];
}

const defaultSlides: SlideData[] = [
  {
    title: 'Master Modern Programming',
    subtitle: 'Learn at Your Own Pace',
    description: 'Comprehensive courses in JavaScript, TypeScript, Python, C#, PHP, Java and more. Expert-led content designed for all skill levels with hands-on projects and real-world applications.',
    icon: <CodeIcon sx={{ fontSize: 100, color: 'primary.main' }} />,
    gradient: 'linear-gradient(135deg, rgba(99, 102, 241, 0.4) 0%, rgba(139, 92, 246, 0.4) 100%)',
    image: 'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=1920&q=80',
    buttonText: 'Explore Courses',
    buttonAction: () => {},
  },
  {
    title: 'Interactive Learning Experience',
    subtitle: 'Test Your Knowledge',
    description: 'Engage with interactive quizzes, track your progress in real-time, and receive instant feedback. Build confidence through practice and comprehensive assessments.',
    icon: <QuizIcon sx={{ fontSize: 100, color: 'secondary.main' }} />,
    gradient: 'linear-gradient(135deg, rgba(236, 72, 153, 0.4) 0%, rgba(251, 113, 133, 0.4) 100%)',
    image: 'https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=1920&q=80',
    buttonText: 'Take a Quiz',
    buttonAction: () => {},
  },
  {
    title: 'Expert-Led Curriculum',
    subtitle: 'Industry Best Practices',
    description: 'Learn from experienced developers and industry experts. Access comprehensive course materials, video tutorials, and hands-on projects that prepare you for real-world challenges.',
    icon: <SchoolIcon sx={{ fontSize: 100, color: 'info.main' }} />,
    gradient: 'linear-gradient(135deg, rgba(59, 130, 246, 0.4) 0%, rgba(96, 165, 250, 0.4) 100%)',
    image: 'https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=1920&q=80',
    buttonText: 'Browse Courses',
    buttonAction: () => {},
  },
  {
    title: 'Track Your Progress',
    subtitle: 'Data-Driven Learning',
    description: 'Monitor your learning journey with detailed analytics and insights. View your quiz history, completion rates, and personalized recommendations to optimize your learning path with Learn2Skill.',
    icon: <TrendingUpIcon sx={{ fontSize: 100, color: 'success.main' }} />,
    gradient: 'linear-gradient(135deg, rgba(16, 185, 129, 0.4) 0%, rgba(52, 211, 153, 0.4) 100%)',
    image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=1920&q=80',
    buttonText: 'View Dashboard',
    buttonAction: () => {},
  },
];

const HomeSlider: React.FC<HomeSliderProps> = ({ slides = defaultSlides }) => {
  const navigate = useNavigate();
  const [activeIndex, setActiveIndex] = useState(0);

  // Auto-advance slides
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % slides.length);
    }, 6000); // Change slide every 6 seconds

    return () => clearInterval(interval);
  }, [slides.length]);

  const handleNext = () => {
    setActiveIndex((prev) => (prev + 1) % slides.length);
  };

  const handlePrevious = () => {
    setActiveIndex((prev) => (prev - 1 + slides.length) % slides.length);
  };

  const handleDotClick = (index: number) => {
    setActiveIndex(index);
  };

  const handleButtonClick = (slide: SlideData) => {
    if (slide.buttonAction) {
      slide.buttonAction();
    } else if (slide.buttonText?.includes('Courses')) {
      navigate('/courses');
    } else if (slide.buttonText?.includes('Dashboard')) {
      navigate('/dashboard');
    } else if (slide.buttonText?.includes('Quiz')) {
      navigate('/quizzes');
    }
  };

  return (
    <SliderContainer>
      {slides.map((slide, index) => (
        <Slide key={index} active={index === activeIndex} gradient={slide.gradient}>
          {slide.image && (
            <SlideImage
              src={slide.image}
              alt={slide.title}
              loading="lazy"
            />
          )}
          <SlideContent>
            <Box
              sx={{
                display: 'inline-flex',
                p: 4,
                borderRadius: 4,
                background: 'rgba(30, 41, 59, 0.8)',
                backdropFilter: 'blur(20px)',
                border: '1px solid rgba(148, 163, 184, 0.2)',
                mb: 4,
                boxShadow: '0 20px 40px rgba(0, 0, 0, 0.5)',
                animation: 'pulse 2s ease-in-out infinite',
                '@keyframes pulse': {
                  '0%, 100%': {
                    transform: 'scale(1)',
                    boxShadow: '0 20px 40px rgba(0, 0, 0, 0.5)',
                  },
                  '50%': {
                    transform: 'scale(1.05)',
                    boxShadow: '0 25px 50px rgba(99, 102, 241, 0.6)',
                  },
                },
              }}
            >
              {slide.icon}
            </Box>
            <Typography
              variant="h3"
              sx={{
                mb: 1,
                fontWeight: 800,
                fontSize: { xs: '2rem', sm: '2.5rem', md: '3.5rem', lg: '4rem' },
                background: 'linear-gradient(135deg, #ffffff 0%, #e2e8f0 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                lineHeight: 1.2,
                letterSpacing: '-0.02em',
                textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
              }}
            >
              {slide.title}
            </Typography>
            <Typography
              variant="h5"
              sx={{
                mb: 3,
                fontWeight: 500,
                fontSize: { xs: '1rem', sm: '1.25rem', md: '1.5rem' },
                color: 'primary.light',
                letterSpacing: '0.05em',
                textTransform: 'uppercase',
                textShadow: '0 2px 4px rgba(0, 0, 0, 0.5)',
              }}
            >
              {slide.subtitle}
            </Typography>
            <Typography
              variant="h6"
              sx={{
                mb: 5,
                maxWidth: '800px',
                mx: 'auto',
                lineHeight: 1.8,
                fontSize: { xs: '1rem', sm: '1.1rem', md: '1.25rem' },
                color: '#f1f5f9',
                fontWeight: 400,
                textShadow: '0 2px 8px rgba(0, 0, 0, 0.5)',
              }}
            >
              {slide.description}
            </Typography>
            {slide.buttonText && (
              <Button
                variant="contained"
                size="large"
                endIcon={<ArrowForwardIcon />}
                onClick={() => handleButtonClick(slide)}
                sx={{
                  px: 5,
                  py: 2,
                  borderRadius: 3,
                  background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                  fontSize: { xs: '1rem', sm: '1.1rem' },
                  fontWeight: 600,
                  boxShadow: '0 12px 32px rgba(99, 102, 241, 0.5)',
                  textTransform: 'none',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
                    transform: 'translateY(-4px)',
                    boxShadow: '0 16px 40px rgba(99, 102, 241, 0.6)',
                  },
                }}
              >
                {slide.buttonText}
              </Button>
            )}
          </SlideContent>
        </Slide>
      ))}

      <NavigationButton className="prev" onClick={handlePrevious} aria-label="Previous slide">
        <ArrowBackIcon />
      </NavigationButton>
      <NavigationButton className="next" onClick={handleNext} aria-label="Next slide">
        <ArrowForwardIcon />
      </NavigationButton>

      <DotsContainer>
        {slides.map((_, index) => (
          <Dot
            key={index}
            active={index === activeIndex}
            onClick={() => handleDotClick(index)}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </DotsContainer>
    </SliderContainer>
  );
};

export default HomeSlider;
